import { inject, Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert, isDefined } from '@sama/js-core';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { AnnotationState } from '../../../../../cross-team-shared/annotation/shared/state/annotation-state';
import { ObjectState } from '../../../../../cross-team-shared/annotation/shared/state/object-state';
import { isShapeStore } from '../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { areTagsStatesEqual } from '../../../../../cross-team-shared/misc-services/tag-helper';
import { debounceOnMicroQueue } from '../../../../../shared/misc-utilities/misc-utils';
import { VisibilityState } from '../../../../../team-apis/otterdam/answers/shape-typings';
import { TaskWorkspaceStore } from '../../../../h3ml/state/task-workspace-store';
import { ObjectVisibilityService } from '../../../../shared/misc-services/object-visibility.service';
import { getVisibilityState } from '../../../../shared/utils/shape-visibility-utils';
import { FocusedObjectService } from './focused-object.service';
import { RootFocusedShapeService } from './root-focused-shape.service';

/**
 * Sibling of FocusedAnnotationService to hackily and temporally implement the
 * same functionality but for the root workspace.
 */
@Injectable()
export class RootFocusedAnnotationService {
  private rootFocusedShapeService = inject(RootFocusedShapeService);
  private focusedObjectService = inject(FocusedObjectService);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private objectVisibilityService = inject(ObjectVisibilityService);

  isAnnotationFocused$() {
    return this.getAnnotationStores$().pipe(
      map(([shapeStore, objectStore]) => !!(objectStore || shapeStore)),
    );
  }

  getAnnotationStore() {
    const selectedObjectStore =
      this.focusedObjectService.getFocusedObjectStore();
    if (selectedObjectStore?.state()) {
      return selectedObjectStore;
    }

    const focusedShapeStore =
      this.rootFocusedShapeService.getFocusedShapeStore();
    if (focusedShapeStore?.state()) {
      return focusedShapeStore;
    }
    return undefined;
  }

  getClass$() {
    return this.getAnnotationStore$().pipe(
      debounceOnMicroQueue(),
      switchMap((annotationStore) => {
        if (!annotationStore) {
          return of(undefined);
        }
        return this.extractClassStore(annotationStore).$;
      }),
    );
  }

  getClassStore() {
    const annotationStore = this.getAnnotationStore();
    assert(
      annotationStore,
      'This is never called when there is no focused annotation',
    );
    return this.extractClassStore(annotationStore);
  }

  getFocusedTags$(): Observable<AnnotationState | undefined> {
    return this.getAnnotationStore$().pipe(
      switchMap((annotationStore) => {
        if (annotationStore) {
          return annotationStore.$;
        }
        return of(undefined);
      }),
      distinctUntilChanged(
        (x, y) => isDefined(x) && isDefined(y) && areTagsStatesEqual(x, y),
      ),
    );
  }

  getLocations$() {
    return this.getAnnotationStores$().pipe(
      switchMap(([shapeStore, objectStore]) => {
        if (objectStore || !shapeStore) {
          return of({ key_locations: [] });
        }
        return shapeStore.$;
      }),
    );
  }

  getVisibility$(frameNumber: number) {
    return this.getAnnotationStores$().pipe(
      switchMap(([shapeStore, objectStore]) => {
        if (shapeStore) {
          return shapeStore.$.pipe(
            filter(isDefined),
            map((shape) => getVisibilityState(shape, frameNumber)),
          );
        }
        if (objectStore) {
          return this.objectVisibilityService.getVisibilityState(
            objectStore,
            frameNumber,
          );
        }
        return of(0 as VisibilityState);
      }),
    );
  }

  getAnnotationStore$() {
    return this.getAnnotationStores$().pipe(
      map(([shapeStore, objectStore]) => objectStore ?? shapeStore),
    );
  }

  getFocusedAnnotationId$() {
    return this.getAnnotationStore$().pipe(
      switchMap((annotationStore: Store<AnnotationState> | undefined) => {
        if (annotationStore) {
          return annotationStore('index').$;
        }
        return of(undefined);
      }),
    );
  }

  getAnnotationStores$() {
    return combineLatest([
      this.rootFocusedShapeService.getFocusedShapeStore$(),
      this.focusedObjectService.getFocusedObjectStore$(),
    ]);
  }

  extractClassStore(annotationStore: Store<AnnotationState>) {
    if (isShapeStore(annotationStore)) {
      const objectStore = this.taskWorkspaceStore.getObjectStore(
        annotationStore.state().parentId!,
      );
      return objectStore('class');
    } else {
      return (annotationStore as Store<ObjectState>)('class');
    }
  }
}
